import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AIChatInterface } from "@/components/host/ai-assistant/AIChatInterface";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";

const AIAssistantPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  // Query to check if AI assistant is enabled
  const { data: siteSettings, isLoading: isCheckingSettings } = useQuery({
    queryKey: ['site-settings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('site_settings')
        .select('ai_assistant_enabled')
        .single();
      
      if (error) throw error;
      return data;
    }
  });

  // Query to check user's subscription
  const { data: subscription, isLoading: isCheckingSubscription } = useQuery({
    queryKey: ['ai-subscription'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from('ai_assistant_subscriptions')
        .select('*')
        .eq('host_id', user.id)
        .single();
      
      if (error && error.code !== 'PGRST116') throw error;
      return data;
    }
  });

  useEffect(() => {
    if (!isCheckingSettings && !siteSettings?.ai_assistant_enabled) {
      toast({
        title: "AI Assistant Unavailable",
        description: "The AI Assistant feature is currently disabled. Please try again later.",
        variant: "destructive"
      });
      navigate('/host');
    }
  }, [siteSettings, isCheckingSettings, navigate, toast]);

  if (isCheckingSettings || isCheckingSubscription) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!subscription?.is_active) {
    return (
      <div className="p-6 max-w-2xl mx-auto text-center">
        <h1 className="text-2xl font-bold mb-4">AI Assistant Subscription Required</h1>
        <p className="text-gray-600 mb-6">
          To use the AI Assistant, you need an active subscription. Please subscribe to access this feature.
        </p>
        <button
          onClick={() => navigate('/host/settings')}
          className="bg-primary text-white px-4 py-2 rounded-md hover:bg-primary/90 transition-colors"
        >
          Manage Subscription
        </button>
      </div>
    );
  }

  return <AIChatInterface />;
};

export default AIAssistantPage;